// Central tracking engine: GDPR-safe loading of Google (GA4 + Google Ads),
// Meta Pixel and LinkedIn Insight Tag. All IDs and on/off switches are managed
// from the admin panel (site_settings -> tracking_config), so nothing is
// hardcoded here. Marketing scripts are only injected after the visitor grants
// consent; Google is loaded with Consent Mode v2 default-denied.

export type TrackingConfig = {
  enabled: boolean;
  consentBannerEnabled: boolean;
  google: { enabled: boolean; ga4Id: string; adsId: string; adsConversionLabel: string };
  meta: { enabled: boolean; pixelId: string };
  linkedin: { enabled: boolean; partnerId: string; conversionId: string };
};

export const DEFAULT_TRACKING_CONFIG: TrackingConfig = {
  enabled: false,
  consentBannerEnabled: true,
  google: { enabled: false, ga4Id: "", adsId: "", adsConversionLabel: "" },
  meta: { enabled: false, pixelId: "" },
  linkedin: { enabled: false, partnerId: "", conversionId: "" },
};

export type ConversionType =
  | "contact_submit"
  | "blueprint_generated"
  | "lead_magnet_view"
  | "case_study_view";

const CONSENT_KEY = "dc_consent_v1"; // "granted" | "denied"
const UTM_KEY = "dc_utm_v1";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_term", "utm_content", "gclid", "fbclid", "li_fat_id"] as const;

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: any;
    _fbq?: any;
    lintrk?: any;
    __PRERENDER__?: boolean;
  }
}

const isBrowser = () => typeof window !== "undefined";
const isPrerender = () => isBrowser() && window.__PRERENDER__ === true;

// ---------------------------------------------------------------------------
// Consent state
// ---------------------------------------------------------------------------
export function getConsent(): "granted" | "denied" | null {
  if (!isBrowser()) return null;
  const v = localStorage.getItem(CONSENT_KEY);
  return v === "granted" || v === "denied" ? v : null;
}

function setConsent(value: "granted" | "denied") {
  if (isBrowser()) localStorage.setItem(CONSENT_KEY, value);
}

// ---------------------------------------------------------------------------
// UTM capture (store first-touch attribution to forward to the contact form)
// ---------------------------------------------------------------------------
export function captureUtm() {
  if (!isBrowser()) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const found: Record<string, string> = {};
    for (const key of UTM_PARAMS) {
      const val = params.get(key);
      if (val) found[key] = val.slice(0, 200);
    }
    if (Object.keys(found).length === 0) return;
    const existing = getUtm();
    // First-touch wins: only store if we don't have attribution yet.
    if (Object.keys(existing).length === 0) {
      localStorage.setItem(UTM_KEY, JSON.stringify({ ...found, landing_page: window.location.pathname }));
    }
  } catch {
    /* ignore */
  }
}

export function getUtm(): Record<string, string> {
  if (!isBrowser()) return {};
  try {
    return JSON.parse(localStorage.getItem(UTM_KEY) || "{}") as Record<string, string>;
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Script loading
// ---------------------------------------------------------------------------
function injectScript(src: string, id: string, attrs: Record<string, string> = {}) {
  if (!isBrowser() || document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v);
  document.head.appendChild(s);
}

let googleBootstrapped = false;
let marketingLoaded = false;
let activeConfig: TrackingConfig = DEFAULT_TRACKING_CONFIG;

// IMPORTANT: must push the native `arguments` object (not a copied array), so
// gtag.js recognises these entries as commands once it loads — this matches
// Google's canonical snippet exactly.
function gtag(..._args: any[]) {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer!.push(arguments);
}

// Loads the Google tag with Consent Mode v2 defaulting to denied. Safe to call
// before consent — no analytics/ads cookies are written until consent updates.
// Order is critical (per Google docs): consent default -> set flags -> config.
function bootstrapGoogle(cfg: TrackingConfig) {
  if (googleBootstrapped || !isBrowser()) return;
  const g = cfg.google;
  const primaryId = g.ga4Id || g.adsId;
  if (!g.enabled || !primaryId) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;
  // 1) Default-denied for all consent signals (EEA-safe, applies to every region).
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
  // 2) Advanced consent-mode recommendations: redact ad data while denied and
  //    pass ad-click/session info via URL when cookies aren't allowed.
  gtag("set", "ads_data_redaction", true);
  gtag("set", "url_passthrough", true);
  // 3) Initialise the tag and configure the properties.
  gtag("js", new Date());
  if (g.ga4Id) gtag("config", g.ga4Id);
  if (g.adsId) gtag("config", g.adsId);

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${primaryId}`, "ga-gtag");
  googleBootstrapped = true;
}

function loadMeta(cfg: TrackingConfig) {
  const m = cfg.meta;
  if (!m.enabled || !m.pixelId || !isBrowser() || window.fbq) return;
  /* Standard Meta Pixel bootstrap */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", m.pixelId);
  window.fbq("track", "PageView");
}

function loadLinkedIn(cfg: TrackingConfig) {
  const l = cfg.linkedin;
  if (!l.enabled || !l.partnerId || !isBrowser() || window.lintrk) return;
  (window as any)._linkedin_partner_id = l.partnerId;
  (window as any)._linkedin_data_partner_ids = (window as any)._linkedin_data_partner_ids || [];
  (window as any)._linkedin_data_partner_ids.push(l.partnerId);
  window.lintrk = function (a: any, b: any) {
    (window.lintrk as any).q.push([a, b]);
  };
  (window.lintrk as any).q = [];
  injectScript("https://snap.licdn.com/li.lms-analytics/insight.min.js", "linkedin-insight");
}

// Called once consent is granted: update Google consent + load marketing pixels.
function loadMarketing(cfg: TrackingConfig) {
  if (marketingLoaded || !isBrowser()) return;
  if (cfg.google.enabled && window.gtag) {
    window.gtag("consent", "update", {
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
    });
  }
  loadMeta(cfg);
  loadLinkedIn(cfg);
  marketingLoaded = true;
}

// ---------------------------------------------------------------------------
// Public init / consent API
// ---------------------------------------------------------------------------
export function initTracking(cfg: TrackingConfig) {
  if (!isBrowser() || isPrerender() || !cfg.enabled) return;
  activeConfig = cfg;
  // Google loads early with consent-mode denied so consent signalling works.
  bootstrapGoogle(cfg);
  // If the visitor already accepted in a previous session, restore everything.
  if (getConsent() === "granted") loadMarketing(cfg);
}

export function acceptConsent() {
  setConsent("granted");
  loadMarketing(activeConfig);
}

export function declineConsent() {
  setConsent("denied");
}

// Best-effort removal of analytics/marketing cookies set by the loaded tags.
// We can't unload third-party scripts already in the page, so revocation pairs
// this cleanup with a reload so nothing re-initialises without fresh consent.
function clearTrackingCookies() {
  if (!isBrowser()) return;
  const prefixes = ["_ga", "_gid", "_gcl", "_fbp", "_fbc", "li_", "bcookie", "lidc", "UserMatchHistory", "AnalyticsSyncHistory"];
  const host = window.location.hostname;
  // Strip leading "www." so we also target the registrable domain (".example.com").
  const baseDomain = host.replace(/^www\./, "");
  const domains = [undefined, host, `.${host}`, baseDomain, `.${baseDomain}`];
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0]?.trim();
    if (!name || !prefixes.some((p) => name.startsWith(p))) continue;
    for (const d of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
    }
  }
}

// Revokes a previously granted consent: persists "denied", signals Consent
// Mode v2 back to denied (Google immediately stops writing ad/analytics
// cookies) and clears tracking cookies. Already-loaded marketing pixels
// (Meta/LinkedIn) cannot be unloaded at runtime, so we reload the page; on the
// next load `loadMarketing` is skipped because consent is now "denied".
export function revokeConsent(options: { reload?: boolean } = {}) {
  const { reload = true } = options;
  setConsent("denied");
  if (isBrowser() && activeConfig.google.enabled && window.gtag) {
    window.gtag("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
  }
  clearTrackingCookies();
  // Reset internal state so a later re-acceptance works within the same session.
  marketingLoaded = false;
  if (reload && isBrowser()) {
    window.location.reload();
  }
}

// ---------------------------------------------------------------------------
// Event + conversion tracking
// ---------------------------------------------------------------------------
export function trackEvent(name: string, params: Record<string, any> = {}) {
  if (!isBrowser() || isPrerender()) return;
  if (window.gtag) window.gtag("event", name, params);
}

export function trackConversion(type: ConversionType, params: Record<string, any> = {}) {
  if (!isBrowser() || isPrerender()) return;
  if (getConsent() !== "granted") return;

  const cfg = activeConfig;

  // --- Google: GA4 event + (optionally) a Google Ads conversion ---
  if (cfg.google.enabled && window.gtag) {
    window.gtag("event", type, params);
    if (type === "contact_submit" && cfg.google.adsId && cfg.google.adsConversionLabel) {
      window.gtag("event", "conversion", {
        send_to: `${cfg.google.adsId}/${cfg.google.adsConversionLabel}`,
        ...params,
      });
    }
  }

  // --- Meta Pixel ---
  if (cfg.meta.enabled && window.fbq) {
    const metaEvent =
      type === "contact_submit" ? "Lead" : type === "blueprint_generated" ? "Lead" : "ViewContent";
    window.fbq("track", metaEvent, params);
  }

  // --- LinkedIn ---
  if (cfg.linkedin.enabled && window.lintrk && cfg.linkedin.conversionId && type === "contact_submit") {
    window.lintrk("track", { conversion_id: Number(cfg.linkedin.conversionId) });
  }
}
